import { useMemo } from 'react';
import { v4 as uuid } from 'uuid';

import { findScopeVariableMatches } from 'components/powerFlowShared/helpers/findScopeVariableMatches';
import { flowNodeColor } from 'fable/flowEditor';
import type { ChatExtraData } from 'fable/flowEditor/components/flowManager/canvas/types/extraData';
import { Text } from 'flow-builder/node/text';
import { TextBreak } from 'flow-builder/node/textBreak';
import { TextContent } from 'flow-builder/node/textContent';
import { hexStringToNumber } from 'flow-builder/renderers/common/colors';
import type {
  GenericNodeAttributes,
  GenericNodeProps,
} from 'flow-builder/types/node';

import { parseHtmlStringToDom } from '../../shared/utils/domParser';
import { MessageModal as MessengerMessageModal } from '../components/messageModal';

export type MessengerMessagePayload = {
  text: string;
  nextNodeId: string;
};

type MessengerMessageNodeAttributes = GenericNodeAttributes<
  MessengerMessagePayload,
  ChatExtraData
>;
type MessengerMessageNodeProps = GenericNodeProps<
  MessengerMessagePayload,
  ChatExtraData
>;

const translateScopeVariables: MessengerMessageNodeAttributes['translateScopeVariables'] =
  (node) => {
    const { payload } = node;

    const needs = findScopeVariableMatches(payload.text);

    return {
      grants: [],
      needs,
    };
  };

const translatePayload: MessengerMessageNodeAttributes['translatePayload'] = (
  node,
) => {
  const { nextNodeId } = node.payload;

  return [
    {
      id: uuid(),
      label: 'arrow',
      connection: nextNodeId || uuid(),
    },
  ];
};

const updateConnection: MessengerMessageNodeAttributes['updateConnection'] = (
  node,
  _connection,
  connectionId,
) => {
  const payload = { ...node.payload, nextNodeId: connectionId };

  return { ...node, payload };
};

export const MessengerMessageNodeAttributes: MessengerMessageNodeAttributes = {
  iconName: 'chat-plus',
  label: 'Message',
  color: hexStringToNumber(flowNodeColor.blue),
  translateScopeVariables,
  translatePayload,
  updateConnection,
  NodeContent: MessengerMessageNode,
  ActionModal: MessengerMessageModal,
};

export function MessengerMessageNode({
  node,
}: MessengerMessageNodeProps): JSX.Element {
  const { payload } = node;
  const { text } = payload;

  return (
    <TextContent maxLines={2}>
      {useMemo(() => {
        const scopeVariables = findScopeVariableMatches(text, [
          'scope',
          'system',
        ]);

        const parsedText = (
          parseHtmlStringToDom(text)?.body?.innerText ?? ''
        ).trim();

        const textWithVars = parsedText.split(/{{(?:scope|system):([^}]+)}}/gm);

        return (
          <TextBreak>
            {textWithVars.map((text, index) => (
              <Text
                key={index}
                weight={scopeVariables.includes(text) ? 'semibold' : 'normal'}
                text={parsedText}
              />
            ))}
          </TextBreak>
        );
      }, [text])}
    </TextContent>
  );
}
